import API from '@api/API';
import { ENDPOINTS } from '@api/endpoints';

import type {
  BexioDisconnectBody,
  BexioExchangeTokenBody,
  BexioExchangeTokenResponse,
  BexioExportResponse,
  BexioRefetchResponse,
} from './types';

export const bexioApi = {
  /** Returns the Bexio OAuth consent URL the user should be redirected to. */
  login: async (): Promise<string> => {
    const res = await API().get<string>(ENDPOINTS.bexio.login());
    return res.data;
  },

  /**
   * Exchanges the `code` received by `/consume` for a server-persisted
   * refresh token associated with the given scope.
   */
  exchangeToken: async (
    code: string,
    body: BexioExchangeTokenBody,
  ): Promise<BexioExchangeTokenResponse> => {
    const res = await API().post<BexioExchangeTokenResponse>(ENDPOINTS.bexio.token(), body, {
      params: { code },
    });
    return res.data;
  },

  /** Triggers a server-side refetch of Bexio data for the given target. */
  refetch: async (auditId: string, targetId: string | number): Promise<BexioRefetchResponse> => {
    const res = await API().post<BexioRefetchResponse>(ENDPOINTS.bexio.refetch(auditId, targetId));
    return res.data;
  },

  /** Reads the audit-level background refetch status for a Bexio target. */
  refetchStatus: async (
    auditId: string,
    targetId: string | number,
  ): Promise<BexioRefetchResponse | null> => {
    const res = await API().get<BexioRefetchResponse | null>(
      ENDPOINTS.bexio.refetchStatus(auditId, targetId),
    );
    return res.data;
  },

  /** Starts a background Bexio export, or returns the latest fresh export job. */
  exportData: async (auditId: string, targetId: string | number): Promise<BexioExportResponse> => {
    const res = await API().post<BexioExportResponse>(ENDPOINTS.bexio.export(auditId, targetId));
    return res.data;
  },

  /** Reads the audit-level background export status for a Bexio target. */
  exportStatus: async (
    auditId: string,
    targetId: string | number,
    jobId?: number | null,
  ): Promise<BexioExportResponse | null> => {
    const res = await API().get<BexioExportResponse | null>(
      ENDPOINTS.bexio.exportStatus(auditId, targetId),
      jobId ? { params: { jobId } } : undefined,
    );
    return res.data;
  },

  /** Revokes the server-stored refresh token for the given scope. */
  disconnect: async (body: BexioDisconnectBody): Promise<void> => {
    await API().post(ENDPOINTS.bexio.disconnect(), body);
  },
};
